import { useState, useRef } from "react";
import { apiFetch } from "../config/api";
import * as XLSX from "xlsx";
import { PRIORITY_OPTIONS, PRIORITY_COLORS, PRIORITY_BG } from "../config/constants";

const TEMPLATE_HEADERS = ["title", "brief", "assigneeEmail", "dueDate", "priority", "expectedOutcome"];

// ── CSV parser (RFC 4180 compliant) ──────────────────────────────────────────
// Handles quoted fields with embedded commas, newlines, and escaped quotes.
// The old line-split approach broke whenever a Google Sheets cell contained
// a newline — each line fragment became a spurious extra task row.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const n = text.trimEnd().length;

  while (i < n) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < n && text[i + 1] === '"') {
          field += '"'; i += 2; // escaped quote ""
        } else {
          inQuotes = false; i++;
        }
      } else {
        field += ch; i++;
      }
    } else {
      if (ch === '"') {
        inQuotes = true; i++;
      } else if (ch === ',') {
        row.push(field); field = ""; i++;
      } else if (ch === '\r' || ch === '\n') {
        if (ch === '\r' && i + 1 < n && text[i + 1] === '\n') i++;
        row.push(field); field = "";
        rows.push(row); row = []; i++;
      } else {
        field += ch; i++;
      }
    }
  }
  // flush last field/row
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }

  // Drop completely empty trailing rows
  while (rows.length && rows[rows.length - 1].every(c => !c.trim())) rows.pop();

  if (rows.length < 2) throw new Error("CSV must have a header row and at least one data row");

  const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/[\s_]+/g, ""));
  return rows.slice(1)
    .filter((r) => r.some((c) => c.trim())) // skip blank rows
    .map((rowArr, i) => {
      const row = {};
      headers.forEach((h, idx) => { row[h] = (rowArr[idx] ?? "").trim(); });
      row._rowNum = i + 2;
      return row;
    });
}

// ── Shared SheetJS sheet parser ───────────────────────────────────────────────
// Used by both local Excel uploads and Google Sheets imports.
function parseWorkbookSheet(workbook, sheetName) {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error(`Sheet "${sheetName}" not found`);
  const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  if (raw.length < 2) throw new Error(`Sheet "${sheetName}" must have a header row and at least one data row`);
  const headers = raw[0].map((h) => String(h).trim().toLowerCase().replace(/"/g, "").replace(/[\s_]+/g, ""));
  return raw.slice(1)
    .filter((r) => r.some((c) => String(c).trim()))
    .map((rowArr, i) => {
      const row = {};
      headers.forEach((h, idx) => {
        let val = rowArr[idx] ?? "";
        if (val instanceof Date) val = val.toISOString().split("T")[0];
        row[h] = String(val).trim();
      });
      row._rowNum = i + 2;
      return row;
    });
}

// ── Excel (.xlsx / .xls) parser using SheetJS ─────────────────────────────────
async function parseExcel(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error("Excel file has no sheets");
  return parseWorkbookSheet(workbook, sheetName);
}

// Normalize dates to YYYY-MM-DD before sending to the API.
// Handles: ISO (2026-06-01), US (12/31/2025), DD/MM/YYYY (31/12/2025), DD/MM (31/12 — assumes current year).
function normalizeDate(val) {
  if (!val) return null;
  const s = String(val).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // already ISO

  // DD/MM/YYYY or DD/MM — always treat as DD/MM (Indian locale standard)
  const dmyMatch = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/);
  if (dmyMatch) {
    const day = dmyMatch[1].padStart(2, "0");
    const mon = dmyMatch[2].padStart(2, "0");
    const yr  = dmyMatch[3] || new Date().getFullYear();
    return `${yr}-${mon}-${day}`;
  }

  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    // Use local date parts — toISOString() would shift the date in non-UTC timezones
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return s; // pass through as-is; backend will try to parse
}

function downloadTemplate() {
  const rows = [
    TEMPLATE_HEADERS.join(","),
    "Fix login bug,Resolve auth failure on mobile,john@company.com,2025-12-31,High,User can login successfully (dates: YYYY-MM-DD or DD/MM/YYYY)",
    "Write API docs,,jane@company.com,2025-11-30,Medium,Documentation complete",
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "bulk_tasks_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function BulkTaskUploadModal({ open, onClose, onCreated, projectId = null }) {
  const [step, setStep] = useState("upload"); // upload | preview | results
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [results, setResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [fetchingSheets, setFetchingSheets] = useState(false);
  const [loadedWorkbook, setLoadedWorkbook] = useState(null); // parsed XLSX workbook
  const [sheetNames, setSheetNames] = useState([]); // tabs in loaded workbook
  const [selectedSheet, setSelectedSheet] = useState("");
  const [activeTab, setActiveTab] = useState("file"); // file | google | manual
  const [manualRows, setManualRows] = useState([{ title: "", assigneeEmail: "", dueDate: "", priority: "Medium", expectedOutcome: "" }]);
  const fileRef = useRef();

  function reset() {
    setStep("upload");
    setRows([]);
    setErrors([]);
    setResults([]);
    setGoogleSheetUrl("");
    setFetchingSheets(false);
    setLoadedWorkbook(null);
    setSheetNames([]);
    setSelectedSheet("");
    setManualRows([{ title: "", assigneeEmail: "", dueDate: "", priority: "Medium", expectedOutcome: "" }]);
    if (fileRef.current) fileRef.current.value = "";
  }

  function handleClose() {
    const hasUnsaved = step === "upload" && activeTab === "manual" && manualRows.some(r => r.title.trim());
    if (hasUnsaved && !window.confirm("Discard unsaved rows and close?")) return;
    reset();
    onClose();
  }

  // FIX BUG-1: detect file type and use the correct parser
  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErrors([]);
    try {
      const ext = file.name.split(".").pop().toLowerCase();
      let parsed;
      if (ext === "xlsx" || ext === "xls") {
        parsed = await parseExcel(file);
      } else {
        const text = await file.text();
        parsed = parseCsv(text);
      }
      setRows(parsed);
      validateRows(parsed);
      setStep("preview");
    } catch (err) {
      setErrors([{ row: 0, message: err.message }]);
    }
  }

  async function handleGoogleSheetLoad() {
    if (!googleSheetUrl.trim()) return;
    setErrors([]);
    setLoadedWorkbook(null);
    setSheetNames([]);
    setSelectedSheet("");

    const match = googleSheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    if (!match) {
      setErrors([{ row: 0, message: "Invalid Google Sheets URL. Paste the full URL from your browser address bar." }]);
      return;
    }

    // Fetch as XLSX so we get ALL sheets in one request and can list tabs.
    const xlsxUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=xlsx`;
    setFetchingSheets(true);
    try {
      const res = await fetch(xlsxUrl);
      if (!res.ok) throw new Error("Could not fetch sheet. Make sure it is set to 'Anyone with the link can view'.");
      const buffer = await res.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      if (!workbook.SheetNames.length) throw new Error("Spreadsheet has no sheets.");

      setLoadedWorkbook(workbook);
      setSheetNames(workbook.SheetNames);

      // If only one sheet, load it directly without showing the selector.
      if (workbook.SheetNames.length === 1) {
        const parsed = parseWorkbookSheet(workbook, workbook.SheetNames[0]);
        setRows(parsed);
        validateRows(parsed);
        setStep("preview");
      } else {
        setSelectedSheet(workbook.SheetNames[0]);
      }
    } catch (err) {
      setErrors([{ row: 0, message: err.message }]);
    } finally {
      setFetchingSheets(false);
    }
  }

  function handleSheetSelect() {
    if (!loadedWorkbook || !selectedSheet) return;
    try {
      const parsed = parseWorkbookSheet(loadedWorkbook, selectedSheet);
      setRows(parsed);
      validateRows(parsed);
      setStep("preview");
    } catch (err) {
      setErrors([{ row: 0, message: err.message }]);
    }
  }

  // FIX BUG-3: was checking !row.assigneeemail && !row.assigneeemail (identical — always true).
  // Now correctly warns when assignee email is missing so the user knows tasks may be unassigned.
  function validateRows(data) {
    const errs = [];
    data.forEach((row) => {
      if (!row.title) errs.push({ row: row._rowNum, message: `Row ${row._rowNum}: Title is required` });
      // assigneeEmail is stored lowercase by CSV/Excel parsers; manual rows use camelCase —
      // check both variants so the warning fires regardless of input source.
      const hasEmail = row.assigneeemail || row.assigneeEmail;
      if (!hasEmail) {
        errs.push({ row: row._rowNum, message: `Row ${row._rowNum}: Assignee email is missing — task will be unassigned` });
      }
      if (row.priority && !PRIORITY_OPTIONS.includes(row.priority)) {
        errs.push({ row: row._rowNum, message: `Row ${row._rowNum}: Priority must be High, Medium, or Low` });
      }
    });
    setErrors(errs);
  }

  const MAX_MANUAL_ROWS = 50;

  function handleManualAdd() {
    if (manualRows.length >= MAX_MANUAL_ROWS) {
      setErrors([{ row: 0, message: `Maximum ${MAX_MANUAL_ROWS} rows allowed for manual entry. Use CSV/Excel for larger uploads.` }]);
      return;
    }
    setManualRows([...manualRows, { title: "", assigneeEmail: "", dueDate: "", priority: "Medium", expectedOutcome: "" }]);
  }

  function handleManualChange(idx, field, value) {
    const updated = [...manualRows];
    updated[idx][field] = value;
    setManualRows(updated);
  }

  function handleManualRemove(idx) {
    setManualRows(manualRows.filter((_, i) => i !== idx));
  }

  // FIX BUG-6: manual rows were stored with camelCase keys (assigneeEmail, dueDate, projectName)
  // but the CSV parser lowercases headers (assigneeemail, duedate, projectname).
  // handleSubmit and the preview table tried both variants, but validateRows and the preview
  // assignee cell only checked the lowercase variant, showing "—" for manual-entry assignees.
  // Solution: normalise manual rows to the same lowercase keys as CSV rows on preview.
  function handleManualPreview() {
    const validRows = manualRows
      .filter((r) => r.title.trim())
      .map((r, i) => ({
        title: r.title.trim(),
        assigneeemail: r.assigneeEmail.trim(),   // normalise to lowercase key
        duedate: r.dueDate.trim(),               // normalise to lowercase key
        priority: r.priority,
        expectedoutcome: r.expectedOutcome?.trim() || "",
        _rowNum: i + 2,
      }));
    if (validRows.length === 0) {
      setErrors([{ row: 0, message: "Add at least one task with a title" }]);
      return;
    }
    setRows(validRows);
    validateRows(validRows);
    setStep("preview");
  }

  async function handleSubmit() {
    setUploading(true);
    try {
      const payload = rows.map((r) => ({
        title: r.title,
        brief: (r.brief || "").trim() || null,
        assigneeEmail: (r.assigneeemail || r.assigneeEmail || "").trim(),
        dueDate: normalizeDate(r.duedate || r.dueDate),
        priority: r.priority || "Medium",
        expectedOutcome: r.expectedoutcome || r.expectedOutcome || null,
      }));

      const url = projectId ? `/api/tasks/bulk?projectId=${projectId}` : "/api/tasks/bulk";
      const res = await apiFetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
      setStep("results");
      onCreated(Array.isArray(data) ? data.length : 0);
    } catch (err) {
      setErrors([{ row: 0, message: err.message || "Upload failed" }]);
    } finally {
      setUploading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}
      onKeyDown={(e) => { if (e.key === "Escape") handleClose(); }}>
      <div
        className="glass-card"
        style={{ width: 740, maxHeight: "88vh", overflowY: "auto", padding: 32, borderRadius: 20, boxShadow: "0 30px 80px rgba(0,0,0,0.16)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{ alignItems: "flex-start" }}>
          <div>
            <h3 style={{ margin: 0 }}>📤 Bulk Task Upload</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-3)" }}>
              {step === "upload" && "Upload a CSV, Excel, Google Sheet, or enter tasks manually"}
              {step === "preview" && `Preview ${rows.length} task${rows.length !== 1 ? "s" : ""} before creating`}
              {step === "results" && "Upload complete"}
            </p>
          </div>
          <button className="modal-close" onClick={handleClose}>✕</button>
        </div>

        {/* Step: Upload */}
        {step === "upload" && (
          <div>
            {/* Tabs */}
            <div role="tablist" style={{ display: "flex", gap: 2, marginBottom: 20, background: "var(--surface-2)", borderRadius: 10, padding: 3 }}>
              {[
                { id: "file", label: "📁 CSV / Excel" },
                { id: "google", label: "📊 Google Sheets" },
                { id: "manual", label: "✏️ Manual Entry" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => { setActiveTab(tab.id); setErrors([]); }}
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                    border: "none", cursor: "pointer", transition: "all 0.15s",
                    background: activeTab === tab.id ? "var(--surface)" : "transparent",
                    color: activeTab === tab.id ? "var(--text-1)" : "var(--text-3)",
                    boxShadow: activeTab === tab.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* File Upload */}
            {activeTab === "file" && (
              <div>
                <div
                  style={{
                    border: "2px dashed var(--border-strong)", borderRadius: 14, padding: "40px 24px",
                    textAlign: "center", cursor: "pointer", transition: "all 0.15s",
                    background: "var(--surface-2)",
                  }}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (f) handleFileChange({ target: { files: [f] } });
                  }}
                >
                  <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Drop your CSV or Excel file here</div>
                  <div style={{ fontSize: 13, color: "var(--text-3)" }}>or click to browse</div>
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }} onChange={handleFileChange} />
                </div>
                <div style={{ marginTop: 14, display: "flex", justifyContent: "center" }}>
                  <button className="btn-ghost" style={{ fontSize: 13, padding: "7px 14px", borderRadius: 8 }} onClick={downloadTemplate}>
                    ⬇ Download Template CSV
                  </button>
                </div>
                <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--surface-2)", borderRadius: 10, fontSize: 12, color: "var(--text-3)" }}>
                  <strong style={{ color: "var(--text-2)" }}>Required columns:</strong> Title, Assignee Email &nbsp;|&nbsp;
                  <strong style={{ color: "var(--text-2)" }}>Optional:</strong> Brief, Due Date (YYYY-MM-DD or DD/MM/YYYY), Priority (High/Medium/Low), Expected Outcome
                  <br /><span style={{ fontSize: 11, opacity: 0.8 }}>Column names are flexible — spaces, underscores, and capitalisation are all accepted.</span>
                </div>
              </div>
            )}

            {/* Google Sheets */}
            {activeTab === "google" && (
              <div>
                <div className="form-group">
                  <label className="form-label">Google Sheets URL</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      className="form-input"
                      value={googleSheetUrl}
                      onChange={(e) => { setGoogleSheetUrl(e.target.value); setSheetNames([]); setLoadedWorkbook(null); setSelectedSheet(""); }}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      style={{ flex: 1 }}
                    />
                    <button className="btn" onClick={handleGoogleSheetLoad} disabled={fetchingSheets} style={{ padding: "9px 16px", borderRadius: 10, whiteSpace: "nowrap" }}>
                      {fetchingSheets ? "Loading..." : "Load Sheet"}
                    </button>
                  </div>
                </div>

                {/* Sheet tab selector — shown when workbook has multiple tabs */}
                {sheetNames.length > 1 && (
                  <div style={{ marginBottom: 14, padding: "14px 16px", background: "var(--surface-2)", borderRadius: 10, border: "1px solid var(--border-strong)" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>
                      📋 {sheetNames.length} sheets found — select one to import:
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        className="form-input"
                        value={selectedSheet}
                        onChange={(e) => setSelectedSheet(e.target.value)}
                        style={{ flex: 1 }}
                      >
                        {sheetNames.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                      <button className="btn" onClick={handleSheetSelect} style={{ padding: "9px 16px", borderRadius: 10, whiteSpace: "nowrap" }}>
                        Import Sheet →
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ padding: "12px 14px", background: "var(--brand-soft)", border: "1px solid var(--brand-border)", borderRadius: 10, fontSize: 12, color: "var(--brand-hover)" }}>
                  ⚠ The sheet must be set to <strong>"Anyone with the link can view"</strong>. The sheet must follow the same column format as the CSV template.
                </div>
                <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
                  <button className="btn-ghost" style={{ fontSize: 13, padding: "7px 14px", borderRadius: 8 }} onClick={downloadTemplate}>
                    ⬇ Download Template to See Required Format
                  </button>
                </div>
              </div>
            )}

            {/* Manual Entry */}
            {activeTab === "manual" && (
              <div>
                <div style={{ display: "grid", gap: 10 }}>
                  {/* Header */}
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 0.8fr 32px", gap: 6, padding: "0 4px" }}>
                    {["Title *", "Assignee Email", "Due Date", "Priority", ""].map((h) => (
                      <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
                    ))}
                  </div>

                  {manualRows.map((row, idx) => (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 0.8fr 32px", gap: 6, alignItems: "center" }}>
                      <input placeholder="Task title" value={row.title} onChange={(e) => handleManualChange(idx, "title", e.target.value)} style={{ fontSize: 13 }} />
                      <input placeholder="email@company.com" value={row.assigneeEmail} onChange={(e) => handleManualChange(idx, "assigneeEmail", e.target.value)} style={{ fontSize: 13 }} />
                      <input type="date" value={row.dueDate} min={new Date().toLocaleDateString('en-CA')} onChange={(e) => handleManualChange(idx, "dueDate", e.target.value)} style={{ fontSize: 13 }} />
                      <select value={row.priority} onChange={(e) => handleManualChange(idx, "priority", e.target.value)} style={{ fontSize: 13 }}>
                        <option value="High">🔴 High</option>
                        <option value="Medium">🟡 Medium</option>
                        <option value="Low">🟢 Low</option>
                      </select>
                      <button onClick={() => handleManualRemove(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 16, padding: 0 }} disabled={manualRows.length === 1}>✕</button>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
                  <button className="btn-ghost" style={{ fontSize: 13, padding: "7px 14px", borderRadius: 8 }} onClick={handleManualAdd}>+ Add Row</button>
                  <button className="btn" style={{ fontSize: 13, padding: "7px 16px", borderRadius: 8, fontWeight: 600 }} onClick={handleManualPreview}>Preview Tasks →</button>
                </div>
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--danger-soft)", border: "1px solid var(--danger-border)", borderRadius: 10 }}>
                {errors.map((e, i) => (
                  <div key={i} style={{ fontSize: 13, color: "var(--danger-text)" }}>{e.message}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: Preview */}
        {step === "preview" && (
          <div>
            {projectId && (
              <div style={{ marginBottom: 14, padding: "10px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, fontSize: 13, color: "#1d4ed8" }}>
                All tasks will be added to the current project.
              </div>
            )}
            {errors.length > 0 && (
              <div style={{ marginBottom: 14, padding: "12px 14px", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "var(--warn-text)", marginBottom: 4 }}>⚠ Warnings ({errors.length})</div>
                {errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: "var(--warn-text)" }}>{e.message}</div>)}
              </div>
            )}

            <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 1fr 0.8fr", gap: 0, background: "var(--surface-2)", borderBottom: "1px solid var(--border)" }}>
                {["Title", "Assignee Email", "Due Date", "Priority"].map((h) => (
                  <div key={h} style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
                ))}
              </div>
              {rows.slice(0, 20).map((row, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 1fr 0.8fr", borderBottom: i < rows.length - 1 ? "1px solid var(--border)" : "none", background: i % 2 === 0 ? "var(--surface)" : "var(--surface-2)" }}>
                  <div style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600 }}>{row.title || <span style={{ color: "var(--danger)" }}>⚠ Missing</span>}</div>
                  <div style={{ padding: "9px 12px", fontSize: 12, color: "var(--text-2)" }}>{row.assigneeemail || row.assigneeEmail || "—"}</div>
                  <div style={{ padding: "9px 12px", fontSize: 12, color: "var(--text-2)" }}>{row.duedate || row.dueDate || "—"}</div>
                  <div style={{ padding: "9px 12px", fontSize: 12 }}>
                    <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: PRIORITY_BG[row.priority] || "#f3f4f6", color: PRIORITY_COLORS[row.priority] || "#374151" }}>
                      {row.priority || "Medium"}
                    </span>
                  </div>
                </div>
              ))}
              {rows.length > 20 && (
                <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-3)", textAlign: "center", borderTop: "1px solid var(--border)" }}>
                  +{rows.length - 20} more rows not shown
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button className="btn-ghost" onClick={() => { setStep("upload"); setErrors([]); }} style={{ padding: "9px 16px", borderRadius: 10 }}>← Back</button>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "var(--text-3)" }}>{rows.length} task{rows.length !== 1 ? "s" : ""} ready</span>
                <button className="btn" onClick={handleSubmit} disabled={uploading} style={{ padding: "9px 20px", borderRadius: 10, fontWeight: 600 }}>
                  {uploading ? "Creating..." : `Create ${rows.length} Task${rows.length !== 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step: Results */}
        {step === "results" && (
          <div>
            <div style={{ textAlign: "center", padding: "20px 0 30px" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>
                {results.filter((r) => r.success).length === results.length ? "✅" : "⚠️"}
              </div>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>
                {results.filter((r) => r.success).length} of {results.length} tasks created
              </div>
              <div style={{ fontSize: 13, color: "var(--text-3)" }}>
                {results.filter((r) => !r.success).length > 0 && `${results.filter((r) => !r.success).length} failed`}
              </div>
            </div>

            {results.some((r) => !r.success) && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: "var(--danger-text)" }}>Failed rows:</div>
                {results.filter((r) => !r.success).map((r, i) => (
                  <div key={i} style={{ padding: "8px 12px", background: "var(--danger-soft)", borderRadius: 8, fontSize: 13, marginBottom: 4, color: "var(--danger-text)" }}>
                    Row {r.rowIndex + 1}: {r.error}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn-ghost" onClick={reset} style={{ padding: "9px 16px", borderRadius: 10 }}>Upload More</button>
              <button className="btn" onClick={handleClose} style={{ padding: "9px 20px", borderRadius: 10, fontWeight: 600 }}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
